import { FC, useLayoutEffect } from 'react';

import { resolveMilestoneColumnHoverPoint } from '../model/vesting-graph.hover';
import { VestingGraphSeriesPoint } from '../model/vesting-graph.types';

export type ChartTooltipPortalState = {
  left: number;
  payload: Array<{ payload: VestingGraphSeriesPoint }>;
  top: number;
};

export type ChartPointer = {
  x: number;
  y: number;
};

const isSameTooltip = (prev: ChartTooltipPortalState, next: ChartTooltipPortalState) =>
  prev.left === next.left &&
  prev.top === next.top &&
  prev.payload[0]?.payload.timestamp === next.payload[0]?.payload.timestamp &&
  prev.payload[0]?.payload.calendarTimestamp === next.payload[0]?.payload.calendarTimestamp &&
  prev.payload[0]?.payload.milestoneName === next.payload[0]?.payload.milestoneName;

/**
 * Recharts remounts `content` on every move. Sync placement into parent state and
 * render a stable animated portal outside this tree.
 */
export const VestingGraphTooltipSync: FC<{
  active?: boolean;
  chartShellRef: React.RefObject<HTMLDivElement | null>;
  coordinate?: { x?: number; y?: number };
  enabled?: boolean;
  milestoneChartData?: VestingGraphSeriesPoint[];
  onChange: React.Dispatch<React.SetStateAction<ChartTooltipPortalState | null>>;
  payload?: Array<{ payload: VestingGraphSeriesPoint }>;
  pointer?: ChartPointer | null;
}> = ({
  active,
  chartShellRef,
  coordinate,
  enabled = true,
  milestoneChartData,
  onChange,
  payload,
  pointer,
}) => {
  useLayoutEffect(() => {
    if (!enabled) {
      onChange(null);
      return;
    }

    const coordinateX = pointer?.x ?? coordinate?.x;
    const coordinateY = pointer?.y ?? coordinate?.y;

    if (!active || !payload?.length || coordinateX === undefined || coordinateY === undefined) {
      // Brief grace so Recharts' inactive blip between adjacent bars does not restart enter.
      const timeoutId = window.setTimeout(() => onChange(null), 60);
      return () => window.clearTimeout(timeoutId);
    }

    const shell = chartShellRef.current;
    if (!shell) {
      onChange(null);
      return;
    }

    const rect = shell.getBoundingClientRect();
    const hoveredPoint = payload[0]?.payload;
    const snappedPoint =
      milestoneChartData && hoveredPoint
        ? resolveMilestoneColumnHoverPoint({ chartData: milestoneChartData, point: hoveredPoint })
        : null;
    const next: ChartTooltipPortalState = {
      left: rect.left + coordinateX,
      payload: snappedPoint ? [{ payload: snappedPoint }] : payload,
      top: rect.top + coordinateY,
    };

    onChange((prev) => (prev && isSameTooltip(prev, next) ? prev : next));
  }, [
    active,
    chartShellRef,
    coordinate?.x,
    coordinate?.y,
    enabled,
    milestoneChartData,
    onChange,
    payload,
    pointer?.x,
    pointer?.y,
  ]);

  return null;
};
